import type { PageSettings, PagedResult } from '@ms-cloudpack/remote-cache';
import prompts from 'prompts';

const loadMoreValue = 'load_more';
const moveUpCursor = '\x1b[1A';

interface SelectItemsFromPaginatedResultsParams<T> {
  /** The message to display to the user when selecting an item. */
  message: string;

  /** A function that fetches a page of items. */
  getPage: (pageSettings: PageSettings) => Promise<PagedResult<T>>;

  /** A function that returns a string to display for each item in the list. */
  getTitle: (item: T) => string;

  /** The default page size to use when fetching items. @default 10. */
  defaultPageSize?: number;
}

/**
 * Presents an interactive prompt that lists accumulated items plus a "Load More" option.
 * When "Load More" is selected, it fetches the next page and updates the list.
 *
 * @returns The entity selected by the user or null if nothing is chosen.
 */
export async function selectItemFromPaginatedResults<T>(
  params: SelectItemsFromPaginatedResultsParams<T>,
): Promise<T | null> {
  const { message, getPage, getTitle, defaultPageSize = 10 } = params;
  let continuationToken: string | undefined = undefined;
  let accumulatedItems: T[] = [];
  let response: { selected: string };

  // Do-while loop to continue fetching pages until user selects an item or no more pages are available
  do {
    const previousLength = accumulatedItems.length;

    // If no items have been loaded yet or if more pages exist, load the next page
    if (accumulatedItems.length === 0 || continuationToken) {
      const page = await getPage({ continuationToken, maxPageSize: defaultPageSize });
      accumulatedItems = accumulatedItems.concat(page.items);
      continuationToken = page.continuationToken;
    }

    const choices = accumulatedItems.map((item, index) => ({
      title: getTitle(item),
      value: index.toString(),
    }));

    // If more pages exist, add a "Load More" option at the end
    if (continuationToken) {
      choices.push({
        title: 'Load More',
        value: loadMoreValue,
      });
    }

    // Display the selectable list
    response = await prompts({
      type: 'select',
      name: 'selected',
      message: continuationToken ? `${message} (or choose "Load More" to fetch more results)` : message,
      choices,
      initial: previousLength,
    });

    // Check if the user selected "Load More" or a valid item
    if (response.selected === loadMoreValue) {
      // Move the cursor up to overwrite the prompt with the new list
      process.stdout.write(moveUpCursor);
    } else if (response.selected) {
      // Return the chosen entity
      return accumulatedItems[parseInt(response.selected)];
    } else {
      // In case of no selection, return null
      return null;
    }
  } while (continuationToken && response.selected === loadMoreValue);

  // Return null if no valid selection was made
  return null;
}
